// Lógica de negócio pura do módulo de Ordem de Serviço.
// Extraída de ListaOS / CriarOS / DetalhesOS para permitir testes unitários.
// IMPORTANTE: comportamento idêntico ao original — apenas recortado para reuso.
// (logs de depuração foram omitidos por não fazerem parte do comportamento.)

import { TIPO_ENTREGA, formatarData } from "./osHelpers"

// ============================================
// ListaOS — ordenação
// ============================================

// "DD/MM/AAAA" -> "AAAAMMDD" para ordenar datas corretamente.
export const dataParaOrdenacao = d => {
  if (!d) return ""
  if (d.includes("/")) {
    const [dia, mes, ano] = d.split("/")
    return `${ano}${mes}${dia}`
  }
  return d
}

// Timestamp em ms para ordenação: prefere dataOrdenacao (ISO), senão dataAbertura.
const timestampOrdenacao = os => {
  if (os.dataOrdenacao) {
    const t = new Date(os.dataOrdenacao).getTime()
    if (!Number.isNaN(t)) return t
  }
  const d = dataParaOrdenacao(os.dataAbertura)
  if (!d) return 0
  // AAAAMMDD -> Date aproximado (meio-dia UTC) para comparação estável
  if (/^\d{8}$/.test(d)) {
    const t = Date.UTC(+d.slice(0, 4), +d.slice(4, 6) - 1, +d.slice(6, 8), 12)
    return Number.isNaN(t) ? 0 : t
  }
  const t = new Date(d).getTime()
  return Number.isNaN(t) ? 0 : t
}

// Ordena uma cópia da lista já filtrada por data/status/protocolo.
// Campo "data" usa DT_ULT_ALTER/DT_CRIACAO (dataOrdenacao) para que create/edit
// subam ao topo; empate desempata por id descendente.
// Retorna nova lista (não muta a entrada). Sem sortField, retorna cópia intacta.
export const ordenarOS = (lista, sortField, sortDir) => {
  const ordenada = [...lista]
  if (!sortField) return ordenada
  ordenada.sort((a, b) => {
    if (sortField === "data") {
      const av = timestampOrdenacao(a)
      const bv = timestampOrdenacao(b)
      if (av !== bv) return sortDir === "asc" ? av - bv : bv - av
      const idA = a.id ?? 0
      const idB = b.id ?? 0
      return sortDir === "asc" ? idA - idB : idB - idA
    }
    let av
    let bv
    if (sortField === "status") {
      av = a.status || ""
      bv = b.status || ""
    } else {
      av = (a.protocolo || "").toLowerCase()
      bv = (b.protocolo || "").toLowerCase()
    }
    if (av < bv) return sortDir === "asc" ? -1 : 1
    if (av > bv) return sortDir === "asc" ? 1 : -1
    return 0
  })
  return ordenada
}

// Classe de ícone do indicador de ordenação para uma coluna.
export const sortIndicator = (field, sortField, sortDir) => {
  if (sortField !== field) return "bx-sort-alt-2 text-muted"
  return sortDir === "asc" ? "bx-sort-up" : "bx-sort-down"
}

// ============================================
// ListaOS — exibição / status
// ============================================

// Normaliza datas para exibição na lista (ISO, AAAA-MM-DD ou já em DD/MM/AAAA).
export const formatarDataExibicao = data => formatarData(data)

// Cor do badge a partir do rótulo PT-BR de status (usado na ListaOS).
export const corDoStatus = status => {
  switch (status) {
    case "Em aberto":
      return "warning"
    case "Em andamento":
      return "info"
    case "Concluída":
      return "success"
    case "Cancelada":
      return "danger"
    default:
      return "secondary"
  }
}

// Código de status da API -> rótulo PT-BR exibido na lista.
export const mapearStatusLabel = status => {
  let label = status || "Em aberto"
  if (label === "ABERTO") label = "Em aberto"
  else if (label === "ANDAMENTO") label = "Em andamento"
  else if (label === "CONCLUIDA" || label === "CONCLUIDO") label = "Concluída"
  else if (label === "CANCELADA" || label === "CANCELADO") label = "Cancelada"
  return label
}

// Regra de negócio: só é possível editar OS em aberto ou em andamento.
export const podeEditar = statusLabel =>
  statusLabel === "Em aberto" || statusLabel === "Em andamento"

// ============================================
// ListaOS — filtro e processamento
// ============================================

// Converte "DD/MM/AAAA" ou "AAAA-MM-DD" em "AAAAMMDD" para comparar intervalos.
export const dataFiltroParaCmp = d => {
  if (!d) return ""
  if (d.includes("-")) return d.replace(/-/g, "")
  return dataParaOrdenacao(d)
}

// Aplica busca textual + filtros de status, tipo, condomínio e intervalo de data.
export const filtrarOS = (
  lista,
  {
    searchTerm,
    filterStatus,
    filterTipo,
    filterCondominio,
    filterDataInicio,
    filterDataFim,
  },
) => {
  let resultado = [...lista]

  if (searchTerm) {
    const termo = searchTerm.toLowerCase()
    resultado = resultado.filter(
      os =>
        (os.protocolo || "").toLowerCase().includes(termo) ||
        (os.condominio || "").toLowerCase().includes(termo) ||
        (os.responsavelTecnico || "").toLowerCase().includes(termo) ||
        (os.tipoServico || "").toLowerCase().includes(termo),
    )
  }

  if (filterStatus) {
    resultado = resultado.filter(os => os.status === filterStatus)
  }

  if (filterTipo) {
    resultado = resultado.filter(os => os.tipoServico === filterTipo)
  }

  if (filterCondominio) {
    const oid = parseInt(filterCondominio)
    resultado = resultado.filter(
      os =>
        os.oidEmpresa === oid ||
        os.oidEmpresa === filterCondominio ||
        String(os.oidEmpresa) === String(filterCondominio),
    )
  }

  if (filterDataInicio) {
    const ini = dataFiltroParaCmp(filterDataInicio)
    resultado = resultado.filter(os => {
      const d = dataFiltroParaCmp(os.dataAbertura)
      return d && d >= ini
    })
  }

  if (filterDataFim) {
    const fim = dataFiltroParaCmp(filterDataFim)
    resultado = resultado.filter(os => {
      const d = dataFiltroParaCmp(os.dataAbertura)
      return d && d <= fim
    })
  }

  return resultado
}

// Mapeia a resposta crua da API para o formato consumido pela tabela.
export const processarOrdensServico = (raw, tiposServico, condominios) => {
  if (!raw || raw.length === 0) return []

  return raw.map(os => {
    let tipoNome = "Não informado"
    if (os.TIPO_ORDEM) {
      const tipo = tiposServico.find(
        t =>
          t.OID_TIPO === os.TIPO_ORDEM ||
          t.OID_TIPO === parseInt(os.TIPO_ORDEM),
      )
      tipoNome = tipo?.NAME || `Tipo ${os.TIPO_ORDEM}`
    }

    let condominioNome = "Não informado"
    if (os.OID_EMPRESA) {
      const cond = condominios.find(
        c =>
          c.OID_EMPRESA === os.OID_EMPRESA ||
          c.OID_EMPRESA === parseInt(os.OID_EMPRESA),
      )
      condominioNome =
        cond?.NM_RAZAO_SOCIAL ||
        cond?.DS_EMPRESA ||
        `Empresa ${os.OID_EMPRESA}`
    }

    const protocolo = os.PROTOCOLO || `OS-${String(os.OID_OS).padStart(4, "0")}`

    const statusMapeado = mapearStatusLabel(os.STATUS)

    const dataFormatada = formatarData(os.DT_CRIACAO || os.DT_ULT_ALTER)

    // Ordenação: última alteração (ou criação) para create/edit irem ao topo
    const dataOrdenacao = os.DT_ULT_ALTER || os.DT_CRIACAO || null

    return {
      id: os.OID_OS,
      protocolo: protocolo,
      dataAbertura: dataFormatada,
      dataOrdenacao,
      status: statusMapeado,
      condominio: condominioNome,
      oidEmpresa: os.OID_EMPRESA || null,
      responsavelTecnico: os.RESPONSAVEL || "Não informado",
      tipoServico: tipoNome,
      tipoOrdemId: os.TIPO_ORDEM,
      descricao: os.DS_ORDEM || "",
      item: os.DS_ITEM || "",
      equipamentos: [],
      assinatura: null,
    }
  })
}

// ============================================
// DetalhesOS — lookups e regra de conclusão
// ============================================

export const getNomeTipo = (tiposServico, tipoId) => {
  const tipo = tiposServico.find(
    t => t.OID_TIPO === tipoId || t.OID_TIPO === parseInt(tipoId),
  )
  return tipo?.NAME || `Tipo ${tipoId}`
}

export const getNomeCondominio = (condominios, empresaId) => {
  const cond = condominios.find(
    c => c.OID_EMPRESA === empresaId || c.OID_EMPRESA === parseInt(empresaId),
  )
  return cond?.NM_RAZAO_SOCIAL || cond?.DS_EMPRESA || `Empresa ${empresaId}`
}

// Regra de negócio: OS concluída bloqueia editar/excluir.
export const isConcluida = status =>
  status === "CONCLUIDA" || status === "CONCLUIDO"

// ============================================
// CriarOS — protocolo, data e validações
// ============================================

// Protocolo no formato RRRRDDMMYYYY (4 aleatórios + DDMMYYYY).
// Recebe data e gerador aleatório para ser determinístico em testes.
export const gerarProtocolo = (hoje = new Date(), randomFn = Math.random) => {
  const dia = String(hoje.getDate()).padStart(2, "0")
  const mes = String(hoje.getMonth() + 1).padStart(2, "0")
  const ano = hoje.getFullYear()
  const random = String(Math.floor(randomFn() * 10000)).padStart(4, "0")
  return `${random}${dia}${mes}${ano}`
}

// Data de hoje no formato AAAA-MM-DD (input date).
export const formatarDataInput = (hoje = new Date()) => {
  const ano = hoje.getFullYear()
  const mes = String(hoje.getMonth() + 1).padStart(2, "0")
  const dia = String(hoje.getDate()).padStart(2, "0")
  return `${ano}-${mes}-${dia}`
}

// Valida um novo item (nome + quantidade > 0). Retorna a regra, sem efeitos.
export const validarNovoItem = (nome, quantidade) => {
  if (!nome || !nome.trim()) {
    return { valido: false, mensagem: "Digite o nome do item" }
  }
  if (!quantidade || parseInt(quantidade) <= 0) {
    return { valido: false, mensagem: "Digite uma quantidade válida" }
  }
  return {
    valido: true,
    mensagem: null,
    nome: nome.trim(),
    quantidade: parseInt(quantidade),
  }
}

// Valida o formulário de criação de OS. Mantém a mesma ordem de verificação
// e as mesmas mensagens do original; assinatura só é exigida para TIPO_ENTREGA.
export const validarFormularioOS = ({
  condominioSelecionado,
  responsavelTecnico,
  tipoServico,
  descricao,
  assinatura,
  tiposServico,
}) => {
  if (!condominioSelecionado) {
    return { valido: false, mensagem: "Selecione um condomínio" }
  }
  if (!responsavelTecnico) {
    return { valido: false, mensagem: "Preencha o responsável técnico" }
  }
  if (!tipoServico) {
    return { valido: false, mensagem: "Selecione o tipo de serviço" }
  }
  if (!descricao) {
    return { valido: false, mensagem: "Preencha a descrição do serviço" }
  }

  const tipoSelecionado = (tiposServico || []).find(
    t => t.OID_TIPO === parseInt(tipoServico),
  )
  const requerAssinatura = tipoSelecionado?.NAME === TIPO_ENTREGA

  if (requerAssinatura && !assinatura) {
    return {
      valido: false,
      mensagem:
        "É necessário assinar a ordem de serviço para este tipo de serviço",
    }
  }

  return { valido: true, mensagem: null }
}
